import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { TRPC_MAX_BATCH_SIZE } from '@promanage/contracts/transport';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { handleAuthExpiry } from '../features/auth/expiry';
import { claimEarlySession, trpcBaseUrl } from './earlySession';
import { isAuthError, isForbidden } from './errors';
import {
  reportMutationFailure,
  reportQueryFailure,
  resolveMutation,
  resolveQuery,
} from './errorNotices';
// TYPE ONLY, and it must stay that way.
//
// This is the whole reason the API is tRPC: the client's types come from the server's actual
// implementation with no codegen and no schema to keep in step. `verbatimModuleSyntax` makes
// the erasure explicit — a plain `import` from this path would pull Fastify, argon2 and the
// Postgres driver into the browser bundle, so if you ever need a VALUE from the API, the
// answer is to move it into @promanage/contracts or @promanage/core instead.
import type { AppRouter } from '@promanage/api/router';

export type RouterOutputs = inferRouterOutputs<AppRouter>;
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * What a query or mutation may say about its own failure. Read by `lib/errorNotices.ts`.
 *
 * REGISTERED, so `meta` is type-checked at every call site instead of being an untyped bag — a
 * misspelt `errorTitel` would otherwise compile, do nothing, and leave that failure reading
 * "That could not be saved" for ever.
 */
interface ErrorMeta extends Record<string, unknown> {
  /** What was being done, as the notice's title: "Could not withdraw that invoice". */
  errorTitle?: string;
  /**
   * `false` for the rare call whose failure is part of normal rendering rather than an error —
   * and nothing else, so the opt-out is visible in review. Nothing in the console uses it today.
   */
  errorToast?: false;
  /**
   * What only this call knows about its own failure, replacing the server's sentence when it
   * returns one. For the rare case where the right advice depends on the kind of refusal —
   * `financial.entry.tsx`'s batch post, which must say "check the ledger" unless the refusal is
   * provably decided before anything was written.
   */
  errorDetail?: (error: unknown) => string | null;
  /**
   * A mutation that checks what somebody typed — signing in, an authenticator code, a new
   * password. Its UNAUTHORIZED is "that was wrong", reported as a notice, and never read as an
   * expired session.
   */
  credentials?: true;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: ErrorMeta;
    mutationMeta: ErrorMeta;
  }
}

export const queryClient = new QueryClient({
  /*
   * THE ONLY PLACE A FAILED REQUEST IS NOTICED, and that is the point of it: every query and
   * every mutation in the console reports here, so an error cannot go unshown because the
   * component that raised it had no error state. See `lib/errorNotices.ts`.
   *
   * An expired session is the one failure that is ACTED ON rather than shown. The router guard
   * only re-checks `auth.me` on navigation, and caches that check for 60s — a session that
   * expires mid-page is caught here instead, by whichever request the server refuses first.
   * See features/auth/expiry.ts for why a page-level catch isn't enough.
   */
  queryCache: new QueryCache({
    onSuccess(_data, query) {
      resolveQuery(query.queryHash);
    },
    onError(error, query) {
      if (isAuthError(error)) {
        handleAuthExpiry(
          `${window.location.pathname}${window.location.search}${window.location.hash}`,
        );
        return;
      }
      reportQueryFailure(error, {
        key: query.queryHash,
        meta: query.meta,
        hadData: query.state.data !== undefined,
      });
    },
  }),
  mutationCache: new MutationCache({
    onSuccess(_data, _variables, _onMutateResult, mutation) {
      const key = mutation.options.mutationKey;
      if (key !== undefined) resolveMutation(JSON.stringify(key));
    },
    onError(error, _variables, _onMutateResult, mutation) {
      if (isAuthError(error) && mutation.meta?.credentials !== true) {
        handleAuthExpiry(
          `${window.location.pathname}${window.location.search}${window.location.hash}`,
        );
        return;
      }
      const key = mutation.options.mutationKey;
      reportMutationFailure(error, {
        key: key === undefined ? null : JSON.stringify(key),
        meta: mutation.meta,
      });
    },
  }),
  defaultOptions: {
    queries: {
      // Financial data goes stale fast, but not instantly — tuned per-resource at the
      // query level. This is a conservative floor, not a target.
      staleTime: 30_000,
      /*
       * OFFLINE, FAIL RATHER THAN WAIT.
       *
       * React Query's default PAUSES a request while the browser says it is offline and resumes it
       * when signal returns. For a page that is a fine idea and for a route LOADER it is a hang:
       * every loader that prefetches awaits a promise that will not settle until the phone finds a
       * mast, so the installed app opened in a dead zone sat on a blank screen — the offline shell
       * and the inspections kept on the device unreachable behind it. `offlineFirst` makes the one
       * attempt, which fails at once as a connection failure (one notice for all of them — see
       * errorNotices.ts), and `refetchOnReconnect` fetches it again when the signal is back.
       */
      networkMode: 'offlineFirst',
      retry(failureCount, error) {
        // Never retry a refusal. A 401 means the session is gone and a 403 means the answer
        // will not change; retrying both delays the login redirect and triples the log noise
        // on every expired tab left open overnight.
        if (isAuthError(error) || isForbidden(error)) return false;
        // Nor with no network: under `offlineFirst` a retry is what pauses, and the reconnect
        // refetch is the retry that can actually work.
        if (typeof navigator !== 'undefined' && !navigator.onLine) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Never blind-retry a mutation: a retried journal post is a double-post.
      retry: 0,
    },
  },
});

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      // Same-origin '/trpc' locally, where Vite proxies it. Deployed environments set
      // VITE_API_URL at build time since the API lives on its own origin there — see the
      // `credentials: 'include'` comment below for why that's paired with CORS, not a proxy.
      url: trpcBaseUrl(import.meta.env.VITE_API_URL),
      // The API refuses a larger batch outright, so a busy page is split into several requests.
      maxItems: TRPC_MAX_BATCH_SIZE,
      /**
       * The session is an httpOnly cookie, so it has to be sent explicitly.
       *
       * `include` rather than `same-origin` because in production the API is on its own
       * origin; the server pairs this with an allow-list CORS origin and an origin check on
       * every mutation (apps/api/src/server.ts), which is what stops `include` from being a
       * CSRF invitation. In dev, vite proxies /trpc so it is same-origin anyway.
       */
      fetch: (url, options) => {
        // `signal` is pulled out and re-normalised because tRPC types it as
        // `AbortSignal | undefined` while the DOM's RequestInit wants `AbortSignal | null`.
        // Under `exactOptionalPropertyTypes` those are genuinely different types, and
        // spreading straight through does not compile.
        const { signal, ...rest } = options ?? {};
        // The session check `index.html` already started, if this is the request it answers.
        const early = claimEarlySession(String(url), rest.method);
        if (early) return early;
        return fetch(url, { ...rest, credentials: 'include', signal: signal ?? null });
      },
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({ client: trpcClient, queryClient });

// ── Reading errors ───────────────────────────────────────────────────────────
//
// Moved to `./errors` so `errorNotices.ts` can read them without importing the query client —
// see the note there. Re-exported so every existing `from '../lib/trpc'` still resolves.
export { errorCode, errorMessage, isAuthError, isForbidden, needsMfa, needsStepUp } from './errors';
